"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "@/lib/i18n/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

const SORT_KEYS = [
  { value: "relevance", icon: "✨" },
  { value: "rating", icon: "⭐" },
  { value: "availability", icon: "🔥" },
  { value: "priceAsc", icon: "⬆️" },
  { value: "priceDesc", icon: "⬇️" },
  { value: "distance", icon: "📍" },
] as const;

export function SortMenu({
  currentSort,
  baseParams,
}: {
  currentSort: string;
  baseParams: Record<string, string | string[] | undefined>;
}) {
  const router = useRouter();
  const t = useTranslations("search");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function pick(value: string) {
    setOpen(false);
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(baseParams)) {
      if (k === "sort" || v === undefined) continue;
      params[k] = Array.isArray(v) ? v.join(",") : v;
    }
    if (value !== "relevance") params.sort = value;
    const qs = new URLSearchParams(params).toString();
    router.push(`/recherche${qs ? `?${qs}` : ""}` as never);
  }

  const current = SORT_KEYS.find((o) => o.value === currentSort) ?? SORT_KEYS[0];
  const currentLabel = t(`sortOptions.${current.value}.label` as never) as string;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("sortMenu.ariaPrefix", { label: currentLabel })}
        className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full border border-[var(--sahha-border-default)] bg-white px-3 text-[13px] font-medium text-[var(--sahha-text-800)] shadow-[0_2px_8px_-2px_rgba(15,30,58,0.08)] transition-all hover:border-[var(--sahha-primary-300)] active:scale-[0.97] sm:px-3.5"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-[var(--sahha-primary-700)]" fill="none" aria-hidden>
          <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="hidden sm:inline text-[var(--sahha-text-500)]">{t("sortMenu.triggerPrefix")}</span>
        <span className="hidden sm:inline">{currentLabel}</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute end-0 top-[calc(100%+8px)] z-40 w-64 overflow-hidden rounded-2xl border border-[var(--sahha-border-default)] bg-white p-1.5 shadow-[0_20px_60px_-10px_rgba(15,30,58,0.25)]"
        >
          <p className="px-3 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-widest text-[var(--sahha-text-400)]">
            {t("sortMenu.menuHeading")}
          </p>
          {SORT_KEYS.map((opt) => {
            const active = opt.value === currentSort;
            const label = t(`sortOptions.${opt.value}.label` as never) as string;
            const desc = t(`sortOptions.${opt.value}.desc` as never) as string;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => pick(opt.value)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm",
                  active
                    ? "bg-[var(--sahha-primary-50)]"
                    : "hover:bg-[var(--sahha-surface-1)]",
                )}
              >
                <span aria-hidden className="text-base">{opt.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className={cn("block text-[13px] font-semibold leading-tight", active ? "text-[var(--sahha-primary-800)]" : "text-[var(--sahha-text-900)]")}>
                    {label}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-tight text-[var(--sahha-text-500)]">
                    {desc}
                  </span>
                </span>
                {active && (
                  <svg viewBox="0 0 16 16" className="h-4 w-4 text-[var(--sahha-primary-600)]" fill="none" aria-hidden>
                    <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
